<script setup>
import { ref, onMounted } from 'vue';

const isTyping = ref(false);
const currentLineIndex = ref(0);
const totalLines = 12;
const TYPING_INTERVAL = 300;  // Time between each line
const PAUSE_DELAY = 3000;     // Time to pause while text is visible
const FADE_DURATION = 500;    // Time for fade out animation

onMounted(() => {
    // Start the typing animation for the second editor
    startTypingAnimation();
});

const resetAnimation = () => {
    setTimeout(() => {
        currentLineIndex.value = 0;
        setTimeout(() => {
            startTypingAnimation();
        }, FADE_DURATION);
    }, PAUSE_DELAY);
};

const startTypingAnimation = () => {
    isTyping.value = true;
    const interval = setInterval(() => {
        if (currentLineIndex.value < totalLines) {
            currentLineIndex.value++;
        } else {
            clearInterval(interval);
            isTyping.value = false;
            resetAnimation();
        }
    }, TYPING_INTERVAL);
};
</script>

<template>
    <!-- Dual Editor Section -->
    <div class="w-full max-w-full overflow-hidden">
        <div class="overflow-x-auto">
            <div class="pb-4 pt-6 
                sm:px-6 
                min-w-[400px] sm:min-w-[600px] 
                mx-auto
                flex flex-row 
                gap-1 sm:gap-3
                [&_.editor]:h-[300px]
                [&_.editor]:w-[250px]
                [&_.editor]:max-w-[250px]
                hide-scrollbar
                justify-center
            ">
            <!-- First Editor -->
            <div class="editor bg-white border border-gray-200 py-8 sm:py-6 px-4 relative flex flex-col shadow-lg rounded-xl text-lg">
                <!-- Pill Controls -->
                <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div class="bg-white border shadow-lg rounded-full">
                        <div class="flex items-center px-4 py-2">
                            <div class="flex items-center gap-2 h-5 justify-center">
                                <span class="font-logo font-extrabold text-sm opacity-90">from</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex-1 overflow-y-auto hide-scrollbar">
                    <div class="py-4 px-2 text-left">
                        <div class="space-y-3 text-sm">
                            <div>P1 - "video is too long, couldn't finish"</div>
                            <div>skipping? no way to skip</div>
                            <div>P2 mentioned confusion abt what the product does</div>
                            <div>value prop not clear from tutorial</div>
                            <div>P3 - "I just wanted to try it, not watch a video"</div>
                            <div>all 3 users - didn't understand why they need to watch</div>
                            <div>P1 suggested shorter intro?</div>
                            <div>P2 & P3 - progress indicator would help</div>
                            <div>option to skip to hands-on</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Arrow (hidden on mobile) -->
            <div class="flex justify-center items-center">
                <svg class="w-4 aspect-square text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
            </div>

            <!-- Second Editor -->
            <div class="sm:flex-1 snap-start editor
                bg-white border border-gray-200 h-full
                py-8 sm:py-6 pl-8 pr-4 relative flex flex-col shadow-lg rounded-xl text-lg"
            >
                <!-- Pill Controls -->
                <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div class="bg-white border shadow-lg rounded-full">
                        <div class="flex items-center px-4 py-2">
                            <div class="flex items-center gap-2 h-5 justify-center">
                                <span class="font-logo font-extrabold text-sm opacity-90">to</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex-1 [&_h1]:text-sm [&_h2]:text-sm [&_ul]:text-xs text-left pt-2 overflow-y-scroll">
                    <h1 class="font-semibold mb-3 type-in" :class="{ 'visible': currentLineIndex >= 1 }">Onboarding UX Research Findings</h1>
                    
                    <h2 class="font-medium mt-4 mb-2 type-in" :class="{ 'visible': currentLineIndex >= 2 }">Key Pain Points</h2>

                    <ul class="space-y-1.5">
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 3 }">
                            <span class="mr-2">•</span>
                            <span>Tutorial video length (all participants)</span>
                        </li>
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 4 }">
                            <span class="mr-2">•</span>
                            <span>Inability to skip tutorial content</span>
                        </li>
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 5 }">
                            <span class="mr-2">•</span>
                            <span>Unclear value proposition</span>
                        </li>
                    </ul>

                    <!-- Additional sections for User Needs and Recommendations -->
                    <h2 class="font-medium mt-4 mb-2 type-in" :class="{ 'visible': currentLineIndex >= 6 }">User Needs</h2>
                    <ul class="space-y-1.5">
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 7 }">
                            <span class="mr-2">•</span>
                            <span>Shorter introduction content</span>
                        </li>
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 8 }">
                            <span class="mr-2">•</span>
                            <span>Progress indicators during onboarding</span>
                        </li>
                    </ul>

                    <h2 class="font-medium mt-4 mb-2 type-in" :class="{ 'visible': currentLineIndex >= 9 }">Recommendations</h2>
                    <ul class="space-y-1.5">
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 10 }">
                            <span class="mr-2">•</span>
                            <span>Implement skip option for tutorials</span>
                        </li>
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 11 }">
                            <span class="mr-2">•</span>
                            <span>Create hands-on learning path</span>
                        </li>
                        <li class="flex items-start type-in" :class="{ 'visible': currentLineIndex >= 12 }">
                            <span class="mr-2">•</span>
                            <span>Clarify value proposition upfront</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        </div>
    </div>
</template>

<style scoped>
.type-in {
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease-out;
}

.type-in.visible {
    opacity: 1;
    transform: translateY(0);
}


.type-in:not(.visible) {
    transition-duration: 0.5s;
}

.hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}

.hide-scrollbar::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
}
</style>